/**
 * Runtime application configuration boundary.
 *
 * Deployed containers inject `window.__APP_CONFIG__` via `config.js`,
 * rendered at container start by the frontend image entrypoint
 * (frontend/docker-entrypoint.sh), so one image serves every environment.
 * Local development (`npm start`) has no injected config and falls back to
 * compile-time `process.env.REACT_APP_*` values.
 *
 * @typedef {Object} AppConfig
 * @property {string} apiUrl Base URL for backend API calls (e.g. "/api").
 * @property {string} oidcClientId Keycloak SPA client id (the `platform-spa`
 *   public client, or whichever confidential-less client a deployment names).
 * @property {string} oidcAuthority Keycloak realm authority URL (e.g.
 *   "https://auth.example.com/realms/platform"). No separate tenant id
 *   exists — Keycloak's realm is baked into this URL.
 */

const REQUIRED_KEYS = ['apiUrl', 'oidcClientId', 'oidcAuthority'];

/**
 * Read and validate the injected runtime config, if present.
 *
 * @returns {AppConfig|null} The validated config, or null when no config
 *   was injected (local development).
 * @throws {Error} When `window.__APP_CONFIG__` exists but required keys are
 *   missing or empty — a deployment error that must fail loudly.
 */
export function getRuntimeConfig() {
  if (typeof window === 'undefined' || window.__APP_CONFIG__ === undefined) {
    return null;
  }
  const config = window.__APP_CONFIG__;
  const missing = REQUIRED_KEYS.filter(
    (key) => typeof config[key] !== 'string' || config[key].length === 0
  );
  if (missing.length > 0) {
    throw new Error(
      `window.__APP_CONFIG__ is missing required keys: ${missing.join(', ')}`
    );
  }
  return config;
}

/**
 * @returns {string|undefined} API base URL — injected runtime config first,
 *   then compile-time env (local dev), else undefined so callers apply
 *   their own localhost fallbacks.
 */
export function getApiUrl() {
  const config = getRuntimeConfig();
  if (config) {
    return config.apiUrl;
  }
  return process.env.REACT_APP_API_URL || undefined;
}

/**
 * @returns {string|undefined} Keycloak SPA client id from injected runtime
 *   config, or undefined when none was injected (local development) — the
 *   caller, src/config/authConfig.js, owns the compile-time
 *   `REACT_APP_OIDC_CLIENT_ID` env fallback and the hardcoded local-dev
 *   default, so this getter does not duplicate either.
 */
export function getOidcClientId() {
  const config = getRuntimeConfig();
  return config ? config.oidcClientId : undefined;
}

/**
 * @returns {string|undefined} Keycloak realm authority URL from injected
 *   runtime config, or undefined when none was injected (local development)
 *   — the caller, src/config/authConfig.js, owns the compile-time
 *   `REACT_APP_OIDC_AUTHORITY` env fallback and the hardcoded local-dev
 *   default, so this getter does not duplicate either.
 */
export function getOidcAuthority() {
  const config = getRuntimeConfig();
  return config ? config.oidcAuthority : undefined;
}
